import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Alt-text prefix of a review image in a design message: "![snapshot: label](storage key)". */
export const SNAPSHOT_ALT = 'snapshot:';

const SCREENSHOT_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/projects/design-screenshots`;
const SNAPSHOT_MARKDOWN = /!\[snapshot:\s*([^\]]*)\]\(([^)\s]+)\)/g;

export interface ReviewSnapshot {
  label: string;
  url: string;
}

/** A stored review image's URL: storage keys are served by the design screenshot route. */
export const snapshotUrl = (src: string): string => (
  src.startsWith('design_screenshots/') ? `${SCREENSHOT_BASE}/${src}` : src
);

/** A design message's text without its review images, and the images (the exact ones a review looked at). */
export const splitReviewSnapshots = (content: string): { text: string; snapshots: ReviewSnapshot[] } => {
  const snapshots: ReviewSnapshot[] = [];
  const pattern = new RegExp(SNAPSHOT_MARKDOWN.source, 'g');

  for (let match = pattern.exec(content); match; match = pattern.exec(content)) {
    snapshots.push({ label: match[1].trim(), url: snapshotUrl(match[2]) });
  }

  return { text: content.replace(SNAPSHOT_MARKDOWN, '').trim(), snapshots };
};

interface ReviewSnapshotModalProps {
  snapshots: ReviewSnapshot[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
}

/** A review image at full size over the page, with its label; arrows step through the message's other images. */
export const ReviewSnapshotModal: React.FC<ReviewSnapshotModalProps> = ({ snapshots, index, onIndex, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const count = snapshots.length;
  const shown = snapshots[index];

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight' && count > 1) onIndex((index + 1) % count);
      else if (event.key === 'ArrowLeft' && count > 1) onIndex((index - 1 + count) % count);
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [count, index, onClose, onIndex]);

  if (!shown) return null;

  const step = (event: React.MouseEvent, delta: number) => {
    event.stopPropagation();
    onIndex((index + delta + count) % count);
  };

  // Portalled to the page body: the design panels clip and transform their content, which would trap a fixed overlay.
  return createPortal(
    <div
      className="chat-media-lightbox review-snapshot-modal"
      role="dialog"
      aria-modal="true"
      aria-label={shown.label}
      onClick={onClose}
    >
      <figure className="review-snapshot-modal__figure" onClick={(event) => event.stopPropagation()}>
        <img src={shown.url} alt={shown.label} />
        <figcaption>{count > 1 ? `${shown.label} · ${index + 1} of ${count}` : shown.label}</figcaption>
      </figure>
      {count > 1 && (
        <>
          <button
            type="button"
            className="review-snapshot-modal__step review-snapshot-modal__step--prev"
            aria-label="Previous image"
            onClick={(event) => step(event, -1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="review-snapshot-modal__step review-snapshot-modal__step--next"
            aria-label="Next image"
            onClick={(event) => step(event, 1)}
          >
            ›
          </button>
        </>
      )}
      <button ref={closeRef} type="button" className="chat-media-lightbox__close" aria-label="Close" onClick={onClose}>
        &times;
      </button>
    </div>,
    document.body,
  );
};

/** One review image: a thumbnail that opens the image in a modal (its strip's, when it is part of one). */
export const ReviewSnapshotThumb: React.FC<ReviewSnapshot & { onOpen?: () => void }> = ({ label, url, onOpen }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="review-snapshot"
        title={label}
        aria-label={`Open ${label}`}
        onClick={() => (onOpen ? onOpen() : setOpen(true))}
      >
        <img src={url} alt={label} loading="lazy" />
        <span>{label}</span>
      </button>
      {open && (
        <ReviewSnapshotModal snapshots={[{ label, url }]} index={0} onIndex={() => undefined} onClose={() => setOpen(false)} />
      )}
    </>
  );
};

/** The images a review looked at, under its message; any of them opens in a modal that steps through them all. */
export const ReviewSnapshotStrip: React.FC<{ snapshots: ReviewSnapshot[] }> = ({ snapshots }) => {
  const [open, setOpen] = useState<number | null>(null);

  if (!snapshots.length) return null;

  return (
    <div className="review-snapshot-strip" aria-label="Images the review looked at">
      {snapshots.map((snapshot, index) => (
        <ReviewSnapshotThumb key={snapshot.url} {...snapshot} onOpen={() => setOpen(index)} />
      ))}
      {open !== null && (
        <ReviewSnapshotModal snapshots={snapshots} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </div>
  );
};
